import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { AppLayout } from './AppLayout';
import type { Role } from '../../types';
import type { ReactNode } from 'react';

interface RequireRoleProps {
  /** Roles allowed to view the route. The user must hold at least one. */
  allow: Role[];
  children: ReactNode;
  /** Where to send users who are authenticated but lack the role. */
  redirectTo?: string;
}

/**
 * Route guard that requires authentication AND one of the listed roles.
 *
 * Server-side authorization remains authoritative — this guard only stops the
 * UI from rendering admin screens for users who can't use them, closing the
 * gap where ProtectedRoute checked authentication but not role.
 */
export function RequireRole({
  allow,
  children,
  redirectTo = '/dashboard',
}: RequireRoleProps) {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-navy">
        <LoadingSpinner message="Loading..." />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (!user || !allow.includes(user.role)) {
    return <Navigate to={redirectTo} replace />;
  }

  return <AppLayout>{children}</AppLayout>;
}
